import { Icona } from '../../components/Icona';
import { useEffect, useState } from 'react';
import { ordini } from '../../api/acquisti';
import type { DestinazioneConsegna, DettaglioOrdine, RigaOrdine } from '../../api/acquisti';
import { formattaData, formattaImporto, formattaNumero, formattaPercentuale, oggiIso } from '../../lib/formato';
import { ChiediMotivo } from './ChiediMotivo';
import { PannelloCarico } from './PannelloCarico';
import { StoricoModifiche } from '../registro/StoricoModifiche';
import { usePuo } from '../sessione/ContestoSessione';

interface Props {
  ordineId: string;
  onChiudi: () => void;
  onCambiato: () => void;
}

type Modulo = 'invio' | 'conferma' | 'bolla' | null;
type Motivo = { tipo: 'annulla' } | { tipo: 'riga'; riga: RigaOrdine } | null;

export function SchedaOrdine({ ordineId, onChiudi, onCambiato }: Props) {
  const puoFare = usePuo();
  const [ordine, setOrdine] = useState<DettaglioOrdine | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [modulo, setModulo] = useState<Modulo>(null);
  const [motivoPer, setMotivoPer] = useState<Motivo>(null);
  // L'esito di una bolla appena registrata: dopo il salvataggio la scheda si ricarica e
  // il modulo sparisce, quindi senza questo non resterebbe niente a dire com'e' andata.
  const [bollaRegistrata, setBollaRegistrata] = useState<string | null>(null);

  useEffect(() => {
    let annullato = false;
    setOrdine(null);
    setErrore(null);
    setModulo(null);
    setMotivoPer(null);
    setBollaRegistrata(null);
    ordini
      .leggi(ordineId)
      .then((o) => !annullato && setOrdine(o))
      .catch((e: Error) => !annullato && setErrore(e.message));
    return () => {
      annullato = true;
    };
  }, [ordineId]);

  async function agisci(azione: () => Promise<DettaglioOrdine>) {
    setInCorso(true);
    setErrore(null);
    try {
      setOrdine(await azione());
      setModulo(null);
      onCambiato();
    } catch (e) {
      setErrore((e as Error).message);
    } finally {
      setInCorso(false);
    }
  }

  async function conMotivo(motivo: string) {
    if (!motivoPer) return;
    const azione = motivoPer;
    setMotivoPer(null);

    await agisci(() =>
      azione.tipo === 'annulla'
        ? ordini.annulla(ordineId, motivo)
        : ordini.chiudiResiduoRiga(ordineId, azione.riga.id, motivo),
    );
  }

  if (errore && !ordine) {
    return (
      <aside className="scheda">
        <p className="errore">{errore}</p>
        <button type="button" onClick={onChiudi}>
          Chiudi
        </button>
      </aside>
    );
  }

  if (!ordine) {
    return (
      <aside className="scheda">
        <p className="attesa">Carico l’ordine…</p>
      </aside>
    );
  }

  const dataAttesa = ordine.consegnaConfermata ?? ordine.consegnaRichiesta;

  return (
    <aside className="scheda" aria-label={`Ordine ${ordine.numero ?? ''}`}>
      <header>
        <div>
          <p className="chiave">
            <strong>{ordine.numero ?? 'senza numero'}</strong>
            <span className={`stato stato-${ordine.stato.toLowerCase()}`}>{ordine.stato}</span>
            {ordine.stato === 'Inviato' && (
              <span className={`stato consegna-${ordine.consegna.toLowerCase()}`}>
                {ordine.consegna === 'Attesa' ? 'in attesa' : `consegna ${ordine.consegna}`}
              </span>
            )}
            {ordine.inRitardo && <span className="stato in-ritardo">in ritardo</span>}
          </p>
          <h2>{ordine.fornitore ?? ordine.codiceFornitoreMago}</h2>
          <p className="riferimenti">
            del {formattaData(ordine.dataOrdine)}
            {dataAttesa && (
              <>
                {' · '}
                {/* Vale la data confermata dal fornitore, e solo in sua mancanza quella
                    che gli abbiamo chiesto: dirlo evita di litigare sulla data sbagliata. */}
                atteso il {formattaData(dataAttesa)}
                {ordine.consegnaConfermata ? ' (confermata)' : ' (richiesta)'}
              </>
            )}
            {ordine.riferimentoFornitore && ` · rif. ${ordine.riferimentoFornitore}`}
          </p>
          {ordine.indirizzoConsegna && (
            <p className="riferimenti">consegna a {ordine.indirizzoConsegna}</p>
          )}
        </div>
        <button type="button" className="chiudi" onClick={onChiudi} aria-label="Chiudi la scheda">
          <Icona nome="chiudi" />
        </button>
      </header>

      {ordine.motivoAnnullamento && (
        <p className="avviso">Annullato: {ordine.motivoAnnullamento}</p>
      )}

      {bollaRegistrata && (
        <p className="messaggio">
          Bolla {bollaRegistrata} registrata. I residui qui sotto sono aggiornati.
        </p>
      )}

      <table className="righe-richiesta">
        <thead>
          <tr>
            <th>Cosa</th>
            <th className="destra">Ordinata</th>
            <th className="destra">Netto</th>
            <th className="destra">Ricevuta</th>
            <th className="destra">Residuo</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {ordine.righe.map((riga) => (
            <tr key={riga.id} className={riga.completata ? 'chiusa' : ''}>
              <td>
                <span className="descrizione">{riga.descrizione}</span>
                <span className="dettaglio">
                  {riga.siglaProduttore && (
                    <em className="chiave">
                      {riga.siglaProduttore} {riga.codiceArticoloProduttore}
                    </em>
                  )}
                  {riga.codiceArticoloFornitore && <span>cod. fornitore {riga.codiceArticoloFornitore}</span>}
                  {riga.numeroRichiesta && <span>da {riga.numeroRichiesta}</span>}
                  {riga.commessa && <span>commessa {riga.commessa}</span>}
                  {riga.scontoTesto && <em className="sconto">{riga.scontoTesto}</em>}
                  {riga.prezzoListino !== null && (
                    <span>
                      listino {formattaImporto(riga.prezzoListino)} −{' '}
                      {formattaPercentuale(riga.scontoPercentuale)}
                    </span>
                  )}
                  {riga.quantitaRifiutata > 0 && (
                    <em className="motivo">
                      {formattaNumero(riga.quantitaRifiutata)}{' '}
                      {riga.quantitaRifiutata === 1 ? 'respinto' : 'respinti'}
                    </em>
                  )}
                  {riga.motivoChiusura && <em className="motivo">chiusa: {riga.motivoChiusura}</em>}
                  {riga.eccedenza > 0 && (
                    <em className="motivo">
                      {formattaNumero(riga.eccedenza)} in più dell’ordinato
                    </em>
                  )}
                </span>
              </td>
              <td className="destra">
                {formattaNumero(riga.quantita)} {riga.unitaMisura}
              </td>
              <td className="destra">{formattaImporto(riga.costoNetto)}</td>
              <td className="destra">{formattaNumero(riga.quantitaRicevuta)}</td>
              <td className="destra">{formattaNumero(riga.residuo)}</td>
              <td className="azioni-riga">
                {riga.residuo > 0 && ordine.stato === 'Inviato' && (
                  <button
                    type="button"
                    disabled={inCorso}
                    title="Chiude quello che il fornitore non consegnerà"
                    onClick={() => setMotivoPer({ tipo: 'riga', riga })}
                  >
                    chiudi
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={5}>Totale</td>
            <td className="destra">{formattaImporto(ordine.totale)}</td>
          </tr>
        </tfoot>
      </table>

      {errore && <p className="errore">{errore}</p>}

      {motivoPer && (
        <ChiediMotivo
          domanda={
            motivoPer.tipo === 'annulla'
              ? 'Perché si annulla questo ordine?'
              : `Perché il fornitore non consegnerà «${motivoPer.riga.descrizione}»?`
          }
          etichettaConferma={motivoPer.tipo === 'annulla' ? 'Annulla l’ordine' : 'Chiudi il residuo'}
          inCorso={inCorso}
          onConferma={(motivo) => void conMotivo(motivo)}
          onAnnulla={() => setMotivoPer(null)}
        />
      )}

      {modulo === 'invio' && (
        <ModuloInvio
          ordine={ordine}
          inCorso={inCorso}
          onAnnulla={() => setModulo(null)}
          onInvia={(consegna, indirizzo, referente) =>
            void agisci(() => ordini.invia(ordineId, consegna, indirizzo, referente))
          }
        />
      )}

      {modulo === 'conferma' && (
        <ModuloConferma
          inCorso={inCorso}
          onAnnulla={() => setModulo(null)}
          onConferma={(riferimento, consegna) =>
            void agisci(() => ordini.conferma(ordineId, riferimento, consegna))
          }
        />
      )}

      {modulo === 'bolla' && (
        <PannelloCarico
          ordine={ordine}
          onAnnulla={() => setModulo(null)}
          onRegistrata={(numero) => {
            setModulo(null);
            setBollaRegistrata(numero ?? 'senza numero');
            void agisci(() => ordini.leggi(ordineId));
          }}
        />
      )}

      {/*  Lo storico dell'ordine, per chi può leggere il registro. Chiuso di
           partenza: è la risposta a una domanda che si fa di rado, e aperto occuperebbe
           la scheda tutte le volte in cui non serve. Il nome della tabella è quello del
           database, perché è la chiave con cui il registro è indicizzato.  */}
      {puoFare('VediRegistro') && (
        <details className="storico-documento">
          <summary>Chi ha cambiato cosa</summary>
          <StoricoModifiche tabella="OrdiniFornitore" chiave={ordineId} />
        </details>
      )}

      {modulo === null && !motivoPer && (
        <div className="azioni">
          {ordine.stato === 'Bozza' && puoFare('InviaOrdini') && (
            <button type="button" className="primario" disabled={inCorso} onClick={() => setModulo('invio')}>
              Invia al fornitore
            </button>
          )}

          {ordine.stato === 'Inviato' && !ordine.confermato && puoFare('ConfermaOrdini') && (
            <button type="button" disabled={inCorso} onClick={() => setModulo('conferma')}>
              Registra la conferma
            </button>
          )}

          {ordine.caricabile && ordine.consegna !== 'Completa' && puoFare('RegistraCarichi') && (
            <button type="button" className="primario" disabled={inCorso} onClick={() => setModulo('bolla')}>
              Registra una bolla
            </button>
          )}

          {ordine.stato !== 'Annullato' && puoFare('AnnullaOrdini') && (
            <button
              type="button"
              className="pericolo"
              disabled={inCorso}
              onClick={() => setMotivoPer({ tipo: 'annulla' })}
            >
              Annulla l’ordine
            </button>
          )}
        </div>
      )}
    </aside>
  );
}

/**
 * L'invio vuole la data di consegna richiesta: è il momento in cui l'ordine diventa un
 * impegno verso il fornitore, e un impegno senza una data non si può controllare.
 * Il referente serve solo qui, perché finisce nell'anagrafica congelata sul documento.
 */
function ModuloInvio({
  ordine,
  inCorso,
  onAnnulla,
  onInvia,
}: {
  ordine: DettaglioOrdine;
  inCorso: boolean;
  onAnnulla: () => void;
  onInvia: (consegna: string, indirizzo?: string, referente?: string) => void;
}) {
  const [consegna, setConsegna] = useState(ordine.consegnaRichiesta?.slice(0, 10) ?? '');
  const [indirizzo, setIndirizzo] = useState(ordine.indirizzoConsegna ?? '');
  const [referente, setReferente] = useState('');

  /*  LA CONSEGNA SI SCEGLIE, e parte dalla sede (15/10/2026): «io farei il contrario di
      Hopperix, che di default mette il cantiere ed e' pericolosissimo». La tendina
      propone la sede dell'ordine, i cantieri delle commesse per cui si compra e il
      ritiro al banco; «altro indirizzo» lascia scrivere. Un indirizzo gia' scritto
      sull'ordine resta quello, e la tendina si mette sulla voce che gli corrisponde.  */
  const [destinazioni, setDestinazioni] = useState<DestinazioneConsegna[]>([]);
  const [scelta, setScelta] = useState<string>('altro');

  useEffect(() => {
    let annullato = false;
    ordini
      .destinazioni(ordine.id)
      .then((elenco) => {
        if (annullato) return;
        setDestinazioni(elenco);
        const gia = ordine.indirizzoConsegna;
        const trovata = gia
          ? elenco.findIndex((d) => d.indirizzo === gia)
          : elenco.findIndex((d) => d.tipo === 'Sede');
        if (trovata >= 0) {
          setScelta(String(trovata));
          setIndirizzo(elenco[trovata]!.indirizzo);
        }
      })
      //  Senza l'elenco resta il campo libero, che e' come funzionava prima.
      .catch(() => undefined);
    return () => {
      annullato = true;
    };
  }, [ordine.id, ordine.indirizzoConsegna]);

  return (
    <form
      className="modulo-scheda"
      onSubmit={(e) => {
        e.preventDefault();
        if (consegna) onInvia(consegna, indirizzo.trim() || undefined, referente.trim() || undefined);
      }}
    >
      <h3>Invia al fornitore</h3>
      <p className="nota">
        All’invio l’anagrafica del fornitore viene congelata sul documento: da qui in poi
        l’ordine mostra il nome e l’indirizzo che il fornitore ha letto, anche se in Mago
        cambiano.
      </p>
      <div className="campi">
        <label>
          Consegna richiesta
          <input
            type="date"
            required
            min={oggiIso()}
            value={consegna}
            onChange={(e) => setConsegna(e.target.value)}
          />
        </label>
        <label className="larga">
          Consegna
          {destinazioni.length > 0 && (
            <select
              value={scelta}
              onChange={(e) => {
                setScelta(e.target.value);
                const d = destinazioni[Number(e.target.value)];
                setIndirizzo(d ? d.indirizzo : '');
              }}
            >
              {destinazioni.map((d, i) => (
                <option key={`${d.tipo}-${i}`} value={String(i)}>
                  {d.etichetta}
                </option>
              ))}
              <option value="altro">Altro indirizzo…</option>
            </select>
          )}
          {(destinazioni.length === 0 || scelta === 'altro') && (
            <input
              type="text"
              placeholder="indirizzo di consegna"
              value={indirizzo}
              onChange={(e) => setIndirizzo(e.target.value)}
            />
          )}
          {scelta !== 'altro' && destinazioni[Number(scelta)]?.tipo !== 'Banco' && (
            <span className="dettaglio">{indirizzo}</span>
          )}
        </label>
        <label className="larga">
          Referente
          <input
            type="text"
            placeholder="a chi è indirizzato"
            value={referente}
            onChange={(e) => setReferente(e.target.value)}
          />
        </label>
      </div>
      <div className="azioni">
        <button type="button" disabled={inCorso} onClick={onAnnulla}>
          Lascia stare
        </button>
        <button type="submit" className="primario" disabled={inCorso || !consegna}>
          {inCorso ? 'Invio…' : 'Invia'}
        </button>
      </div>
    </form>
  );
}

function ModuloConferma({
  inCorso,
  onAnnulla,
  onConferma,
}: {
  inCorso: boolean;
  onAnnulla: () => void;
  onConferma: (riferimento: string, consegna?: string) => void;
}) {
  const [riferimento, setRiferimento] = useState('');
  const [consegna, setConsegna] = useState('');

  return (
    <form
      className="modulo-scheda"
      onSubmit={(e) => {
        e.preventDefault();
        if (riferimento.trim()) onConferma(riferimento.trim(), consegna || undefined);
      }}
    >
      <h3>Conferma del fornitore</h3>
      <p className="nota">
        Da qui la data attesa è quella che ha confermato lui: è la data su cui ha senso
        dire che è in ritardo.
      </p>
      <div className="campi">
        <label className="larga">
          Suo riferimento
          <input
            type="text"
            required
            placeholder="il numero che ha messo lui"
            value={riferimento}
            onChange={(e) => setRiferimento(e.target.value)}
          />
        </label>
        <label>
          Consegna confermata
          <input type="date" value={consegna} onChange={(e) => setConsegna(e.target.value)} />
        </label>
      </div>
      <div className="azioni">
        <button type="button" disabled={inCorso} onClick={onAnnulla}>
          Lascia stare
        </button>
        <button type="submit" className="primario" disabled={inCorso || !riferimento.trim()}>
          {inCorso ? '…' : 'Registra'}
        </button>
      </div>
    </form>
  );
}
